import { FC, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'

import MainLayout from 'containers/main-layout/main-layout'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import FiltersPC from '../../filters/filter-pc/filter-pc'
import FiltersMobile from '../../filters/filter-mobile/filter-mobile'
const FilterCards = dynamic(() => import('../../filters/filter-cards/filter-cards'))
const SearchPagination = dynamic(() => import('../../search/search-pagination/search-pagination'))
import NoTargetCards from './no-target-cards/no-target-cards'
import NewslineCard from 'components/cards/newsline-card/newsline-card'
import ProjectCard from 'components/cards/project-card/project-card'
import usePCWindow from 'hooks/usePCWindow'
import useSyncURL from '../useSyncURL'
import useCommonSearch from '../useCommonSearch'

import { getCardsMaterials } from './utils'
import {
  generateConfig,
  Key,
  InitValues,
  LinkComparasion,
  OptionAliases,
  Option,
} from 'constants/search-data/library-search-data'
import { ScreenSize } from 'constants/media-constanst'
import { MaterialTypes, URL_SITE } from 'constants/data'
import { CARDS_ON_PAGE } from 'constants/search-data/search-data'
import { IOptions } from 'constants/types/common.types'
import { ILibrarySearch } from 'constants/types/search.types'
import { PLACEFLAG_ARTICLE } from 'constants/analytic-data'

const LibrarySearch: FC<ILibrarySearch> = ({
  lectures,
  themes,
  specialities,
  selectedOption,
  targetInitParams,
  initCards,
  children,
}: ILibrarySearch) => {
  const isBigPC = usePCWindow(ScreenSize.big)

  const isMobile = usePCWindow(ScreenSize.mobile)

  const [isInitState, setInitState] = useState(true)
  const isTarget = Boolean(targetInitParams)
  const isPodcast = selectedOption === Option.podcast
  const isInteractive = selectedOption === Option.interactive
  const isHideFilter = isPodcast || isInteractive

  const config = useMemo(
    () => generateConfig(lectures, themes, specialities, isTarget),
    [themes, lectures, specialities, isTarget]
  )

  const selectOptions = {
    [Key.LECTURS]: lectures,
    [Key.THEMES]: themes,
    [Key.SPECIALITIES]: isTarget ? undefined : specialities,
  }

  const formatedInitData = useMemo(
    () => ({
      ...InitValues,
      cards: {
        ...InitValues.cards,
        [selectedOption]: initCards?.rows || [],
      },
      allCount: {
        ...InitValues.allCount,
        [selectedOption]: initCards?.count || 0,
      },
    }),
    []
  )

  const { data, isLoading, handleParameterSet, handleReset, handlePageChange } = useCommonSearch({
    InitValues: formatedInitData,
    selectedOption,
    getCardsMaterials,
    selectOptions,
    isInitState,
    setInitState,
    isMobile,
  })

  if (targetInitParams) {
    data.params[selectedOption][Key.SPECIALITIES] = [targetInitParams]
  }

  useSyncURL(selectedOption, OptionAliases, data.params, true)

  const renderedCards = useMemo(
    () =>
      data.cards[selectedOption]?.map((card: any) => {
        const isPodcastInner = card.materialType === MaterialTypes.PODCAST

        const cardProps = {
          ...card,
          previewName: isPodcastInner ? `/${card.id}/podcast_${card.id}.png` : card.previewName,
          selectedThemes: data.params[selectedOption][Key.THEMES] as IOptions[],
          selectedLecturs: data.params[selectedOption].lectures as IOptions[],
        }

        let item = <></>
        let url = ''

        if (isInteractive) {
          item = <ProjectCard {...cardProps} title={cardProps.name} isInteractive />
          url = `${URL_SITE}/${card.link}`
        } else {
          item = (
            <NewslineCard
              {...cardProps}
              materialType={
                selectedOption === Option.podcast ? MaterialTypes.PODCAST : MaterialTypes.ARTICLE
              }
              cutQueryWords={(data.params[selectedOption].inputText as string)
                .toLowerCase()
                .split(' ')}
              withPreview={selectedOption === Option.clinic ? false : true}
            />
          )
          url = `../${LinkComparasion[selectedOption]}/${card.linkTranslation || card.id}`

          if (targetInitParams) {
            url = `${URL_SITE}/${LinkComparasion[selectedOption]}/${card.linkTranslation || card.id}`
          }
        }

        return {
          url,
          origin: PLACEFLAG_ARTICLE,
          item,
        }
      }) || [],
    [data.cards[selectedOption]]
  )

  const filterPCProps = {
    parameters: data.params[selectedOption],
    handleParameterSet,
    handleReset,
    config: config[selectedOption],
    isBigPC,
  }

  const filterMobileProps = {
    parameters: data.params[selectedOption],
    handleParameterSet,
    handleReset,
    config: config[selectedOption],
    allCount: data.allCount[selectedOption],
  }

  const FilterCardsProps = {
    cards: renderedCards,
    handlePageChange,
    isLoading,
    oneColumn: isInteractive,
    component: isTarget ? <NoTargetCards selectedOption={selectedOption} /> : null,
  }

  return (
    <MainLayout isScreenHeight>
      <WidthLimiter noLeftPadding noRightPadding fullHeight>
        <div>
          {children}
          <div>
            {!isHideFilter && <FiltersPC {...filterPCProps} />}
            <FilterCards {...FilterCardsProps} />
          </div>
          {!isLoading && (
            <SearchPagination
              maxPage={Math.ceil(data.allCount[selectedOption] / CARDS_ON_PAGE) || 0}
              currentPage={Number(data.params[selectedOption].page)}
              handleParameterSet={handleParameterSet}
            ></SearchPagination>
          )}
        </div>
      </WidthLimiter>
      {!isBigPC && !isHideFilter && <FiltersMobile {...filterMobileProps} />}
    </MainLayout>
  )
}

export default LibrarySearch
